import { Skeleton } from 'antd';
import { keyBy } from 'lodash';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { ProjectId, User } from 'api/models';
import { Project } from 'api/project';
import { Action, SocketData } from 'api/pushes';
import { useSharedWorkerMultiProjectWsManager } from 'api/pushes/shared-worker-multi-project-ws-manager';
import { FetchSwimlanesResponse, KanbanApi } from 'api/tasks/kanban';
import { Item } from 'components/organisms/kanban-board/group-by';
import { formatDate } from 'components/organisms/projects/project-plan/timeline/helpers';
import { constructNormalizedTask } from 'components/organisms/tasks/common';
import { SingleSelectField } from 'components/organisms/tasks/fields';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { RoutePaths } from 'constant';
import { setDirty, setSelectedTasks } from 'data/sprints';
import { getIsSprintDirty, getSelectedTasks } from 'data/sprints/selectors';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useWindowResize } from 'hooks/use-window-resize';
import { TaskField } from 'stores/fields';
import { Task } from 'stores/tasks';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { Filter } from '../filters/types';
import { useCustomInviteUserToProjectConfirmation } from '../squad-picker/use-invite-user';
import { toastService } from '../toast';
import {
  KanbanBoard as AtomKanbanBoard,
  DropParams,
  KanbanContextParams,
  LayoutFetchResponse,
} from './Kanban';
import cx from './kanban.module.css';
import { PaginatedKanbanBoardProps } from './Kanban/KanbanBoard';
import {
  NewTasksDefaultParamsGetterCallbackType,
  UpdateSwimlaneParams,
} from './Kanban/type';
import { TaskCard } from './task-card';

type TaskSocketData = Pick<Task, 'taskId' | 'currentSprint'>;

type TaskDetails = Task & { id: number };
type KanbanBoardProps = PaginatedKanbanBoardProps<TaskDetails>;

interface BoardProps {
  isColumnHidden?: boolean;
  fields: TaskField[];
  selectedRowField?: string | null;
  selectedColumnField?: string | null;
  filter: GroupedFilter | Filter;
  groupByItems: Item[];
  projectDetail?: Project;
  statusesToShow?: number[];
  readOnly?: boolean;
}

interface IsAllowedParams {
  dropParams: DropParams<TaskDetails>;
  columnGroup?: Item;
  rowGroup?: Item;
  filter: GroupedFilter | Filter;
  fieldList: Record<number, TaskField>;
}

const useAssigneeChange = () => {
  const worker = callWorkerWithCleanup();
  const inviteUser = useCustomInviteUserToProjectConfirmation();

  return useCallback(
    async ({ dropParams, columnGroup, rowGroup }: IsAllowedParams) => {
      const isAssigneeChange =
        columnGroup?.value === 'assignee' || rowGroup?.value === 'assignee';

      if (!isAssigneeChange) {
        return Promise.resolve();
      }
      const isColumnChange =
        columnGroup?.value === 'assignee' &&
        dropParams.to.colId !== dropParams.from.colId;
      const isRowChange =
        rowGroup?.value === 'assignee' &&
        dropParams.to.swimlaneId !== dropParams.from.swimlaneId;

      if (!isColumnChange && !isRowChange) {
        return Promise.resolve();
      }

      let newAssigneeId: number | null = null;

      if (columnGroup?.value === 'assignee') {
        newAssigneeId = Number(dropParams.to.colId);
      } else if (rowGroup?.value === 'assignee') {
        newAssigneeId = Number(dropParams.to.swimlaneId);
      }
      if (!newAssigneeId) {
        return Promise.resolve();
      }
      const projectId = dropParams.task.extra?.project;

      if (!projectId) {
        return Promise.reject();
      }
      const user = (await worker.workerApi.getUserById(
        Number(newAssigneeId),
      )) as User;

      if (!user.userId) {
        return Promise.reject();
      }
      const notInProject = await worker.workerApi.getUsersNotInProject(
        projectId as number,
      );
      const notInProjectUsers = notInProject.map((u) => u.userId);

      if (notInProjectUsers.length === 0) {
        return Promise.resolve();
      }
      if (!notInProjectUsers.includes(user.userId)) {
        return Promise.resolve();
      }

      return new Promise((res, rej) => {
        inviteUser(
          [{ extra: { id: user.userId }, userType: user.userType }],
          [user.userId],
          dropParams.task.extra.assignee?.map((a) => a[1]),
          (userId) => {
            res(userId);
          },
          () => {
            rej();
          },
          ProjectId(dropParams.task.extra.project),
        );
      });
    },
    [inviteUser, worker.workerApi],
  );
};
const useMutateDragAndDrop = () => {
  const handleAssignee = useAssigneeChange();
  const translation = useTranslate();
  const { pathname } = useLocation();
  const isSprint = pathname.includes(RoutePaths.sprintPlanning);

  return useCallback(
    async ({
      dropParams,
      columnGroup,
      rowGroup,
      filter,
      fieldList,
    }: IsAllowedParams): ReturnType<KanbanBoardProps['handleDragAndDrop']> => {
      await handleAssignee({
        dropParams,
        columnGroup,
        rowGroup,
        filter,
        fieldList,
      });
      const response = await KanbanApi.updateDragAndDrop({
        fromColId: dropParams.from.colId as number,
        fromRowId: dropParams.from.swimlaneId as number,
        toRowId: dropParams.to.swimlaneId as number,
        toColId: dropParams.to.colId as number,
        taskId: dropParams.task.id as number,
        columnGroup,
        rowGroup,
        filter,
        isSprint,
      });

      if (response.isDragAndDropPossible) {
        const task = constructNormalizedTask({
          taskList: [response.taskDetails],
          fieldList,
          isLoading: false,
        });
        const taskDetails = task.byId[response.taskDetails.taskId][1];

        if (!taskDetails) {
          return {
            isAllowed: false,
          };
        }

        return {
          isAllowed: true,
          values: response.swimlaneData.map((swimlane) => ({
            columnIds: Object.keys(swimlane.columnData),
            swimlaneId: swimlane.rowId,
          })),
          task: {
            extra: {
              ...(taskDetails as any),
              id: response.taskDetails.taskId,
            },
            id: response.taskDetails.taskId,
            label: response.taskDetails.taskName,
          },
        };
      }
      toastService({
        message: '',
        title: translation('kanban.error.drop_not_allowed.title'),
      }).error();

      return {
        isAllowed: false,
      };
    },
    [handleAssignee, translation, isSprint],
  );
};

const TaskCardWrapper: KanbanBoardProps['taskCardRenderer'] = ({
  extra,
  isUpdating,
  readOnly,
}) => {
  return <TaskCard {...extra} isUpdating={isUpdating} readOnly={readOnly} />;
};

const CardLoading: KanbanBoardProps['taskCardLoadingRenderer'] = ({
  activeLoading,
}) => {
  return (
    <div className={cx.taskLoading}>
      <Skeleton
        paragraph={{
          rows: 1,
          width: '230px',
        }}
        active={activeLoading}
      />
    </div>
  );
};

export const KanbanBoard: FC<BoardProps> = memo(
  ({
    fields,
    selectedColumnField,
    selectedRowField,
    filter,
    groupByItems,
    isColumnHidden,
    projectDetail,
    statusesToShow,
    readOnly = false,
  }) => {
    const { projects } = useUser();
    const projectOptions = useMemo(
      () =>
        (projects ?? []).map((p) => ({
          label: p.projectName || '',
          value: p.projectId,
          imageUrl: p.customer.companyLogoUrl,
        })),
      [projects],
    );

    const projectId = projectDetail?.projectId;

    const { pathname } = useLocation();
    const isSprint = pathname.includes(RoutePaths.sprintPlanning);
    const isSprintDirty = useSelector(getIsSprintDirty);

    const params = useParams<{ sprintId: string }>();
    const sprintId = Number(params.sprintId);

    const entityId = isSprint ? sprintId : projectDetail?.projectId;

    const getPriorityValue = useCallback(
      (id) => {
        const priorityField = fields?.find(
          ({ fieldName }) => fieldName === 'priority',
        ) as SingleSelectField;
        const priorityValue = priorityField?.metaData.options.find(
          ({ value }) => value === id,
        );

        return {
          fieldId: priorityField.fieldId,
          fieldValue: priorityValue?.value,
        };
      },
      [fields],
    );

    const getStatusValue = useCallback(
      (id) => {
        const statusField = fields?.find(
          ({ fieldName }) => fieldName === 'status',
        ) as SingleSelectField;
        const statusValue = statusField?.metaData.options.find(
          ({ value }) => value === id,
        );

        return { fieldId: statusField.fieldId, fieldValue: statusValue?.value };
      },
      [fields],
    );
    const [mount, setMount] = useState(false);
    const rowField = useMemo(() => {
      return groupByItems.find((item) => item.value === selectedRowField);
    }, [groupByItems, selectedRowField]);
    const columnField = useMemo(() => {
      return groupByItems.find((item) => item.value === selectedColumnField);
    }, [groupByItems, selectedColumnField]);

    const taskFieldsLookupById = useMemo(
      () => keyBy(fields, 'fieldId') as Record<number, TaskField>,
      [fields],
    );
    const ref = useRef<any>();
    const boardApi = useRef<KanbanContextParams['kanbanActions']>();

    const dispatch = useDispatch();
    const selectedTasks = useSelector(getSelectedTasks);
    const initialFetchRef = useRef({ isFetchedInitially: false });

    useEffect(() => {
      boardApi.current?.resetBoard();
      setMount(false);
      setTimeout(() => {
        setMount(true);
      });
      if (isSprint) {
        if (initialFetchRef.current.isFetchedInitially) {
          dispatch(setSelectedTasks([]));
        }
        initialFetchRef.current.isFetchedInitially = true;
      }
    }, [selectedColumnField, selectedRowField, filter, dispatch, isSprint]);
    const socketUpdates = useRef(
      async (data: SocketData<TaskSocketData | number[]>) => {
        if (Array.isArray(data.data)) {
          return;
        }
        if (!isSprint && projectId !== data.projectId) {
          return;
        }

        if (isSprint) {
          if (sprintId !== data.data.currentSprint?.sprintId) {
            return;
          }
          if (!isSprintDirty) {
            dispatch(setDirty());
          }
        }
        if (data.action === Action.Delete) {
          boardApi.current?.deleteTask({ taskId: data.data.taskId });

          return;
        }
        const res = await KanbanApi.fetchLayoutForTaskId({
          fieldList: taskFieldsLookupById,
          taskId: data.data.taskId,
          filter,
          columnGroup: columnField,
          rowGroup: rowField,
          id: entityId,
          isSprint,
        });

        if (res.taskDetails) {
          boardApi.current?.handleSocketUpdate({
            swimlaneData: res.swimlaneData,
            taskDetails: { ...res.taskDetails, id: res.taskDetails.taskId },
          });
        }
      },
    );

    useEffect(() => {
      socketUpdates.current = async (
        data: SocketData<TaskSocketData | number[]>,
      ) => {
        if (Array.isArray(data.data)) {
          return;
        }
        if (!isSprint && projectId !== data.projectId) {
          return;
        }

        if (isSprint) {
          if (sprintId !== data.data.currentSprint?.sprintId) {
            return;
          }
          if (!isSprintDirty) {
            dispatch(setDirty());
          }
        }
        if (data.action === Action.Delete) {
          boardApi.current?.deleteTask({ taskId: data.data.taskId });

          return;
        }
        const res = await KanbanApi.fetchLayoutForTaskId({
          fieldList: taskFieldsLookupById,
          taskId: data.data.taskId,
          filter,
          columnGroup: columnField,
          rowGroup: rowField,
          id: entityId,
          isSprint,
        });

        if (res.taskDetails) {
          boardApi.current?.handleSocketUpdate({
            swimlaneData: res.swimlaneData,
            taskDetails: { ...res.taskDetails, id: res.taskDetails.taskId },
          });
        }
      };
    }, [
      columnField,
      filter,
      projectId,
      rowField,
      taskFieldsLookupById,
      entityId,
      isSprint,
      sprintId,
      isSprintDirty,
      dispatch,
    ]);

    useSharedWorkerMultiProjectWsManager(
      useCallback((data) => socketUpdates.current(data), []),
    );

    const { height, reCompute } = useWindowResize((windowHeight) => {
      return windowHeight - ref.current?.getBoundingClientRect().top - 12;
    });

    const onKanbanReady = useCallback((kanban) => {
      boardApi.current = kanban;
    }, []);
    const mutateDragAndDrop = useMutateDragAndDrop();

    const handleDragAndDrop: KanbanBoardProps['handleDragAndDrop'] =
      useCallback(
        (dropParams) =>
          mutateDragAndDrop({
            dropParams,
            columnGroup: columnField,
            rowGroup: rowField,
            filter,
            fieldList: taskFieldsLookupById,
          }),
        [
          mutateDragAndDrop,
          columnField,
          rowField,
          filter,
          taskFieldsLookupById,
        ],
      );

    const layoutFetch = useCallback(async () => {
      const res = await KanbanApi.getLayout({
        columnGroup: columnField,
        rowGroup: rowField,
        filter,
        id: entityId,
        isSprint,
      });
      const defaultOrderColId: string[] = [];

      return {
        swimlane: res.kanbanResponseModelList
          .filter((row) => (isColumnHidden ? row.totalTaskCount : true))
          .map((row) => ({
            id: row.rowId,
            label: row.rowLabel,
            extra: null,
            count: row.totalTaskCount,
            column: Object.keys(row.columnData).map((colId) => {
              if (!defaultOrderColId.includes(colId)) {
                defaultOrderColId.push(colId);
              }

              return {
                count: row.columnData[colId].innerTaskCount,
                extra: null,
                id: colId,
                label: row.columnData[colId].colLabel,
              };
            }) as LayoutFetchResponse<any, any>['swimlane'][0]['column'],
          })),
        orderColIds: res.columnSortOrder || defaultOrderColId,
        filteredCols: statusesToShow,
      } as LayoutFetchResponse<any, any>;
    }, [
      columnField,
      filter,
      isColumnHidden,
      rowField,
      isSprint,
      entityId,
      statusesToShow,
    ]);

    const swimlaneColumnFetch: KanbanBoardProps['swimlaneColumnFetch'] =
      useCallback(
        async ({ columnId, startOffset, swimlaneId }) => {
          const res = await KanbanApi.getTaskInSwimLane({
            offsetCol: startOffset,
            colId: columnId as number,
            rowId: swimlaneId as number,
            columnGroup: columnField,
            rowGroup: rowField,
            fieldList: taskFieldsLookupById,
            filter,
            id: entityId,
            isSprint,
          });

          return (
            res.columnData[columnId]?.tasks.map((task) => ({
              colId: columnId,
              extra: { ...task, id: task.taskId } as any,
              id: task.taskId,
              rowId: swimlaneId,
              isDropped: true,
              label: task.taskName,
              swimlaneId,
            })) ?? []
          );
        },
        [
          columnField,
          filter,
          rowField,
          taskFieldsLookupById,
          isSprint,
          entityId,
        ],
      );

    const getModifiedResponseForSwimlaneFetch = (
      response: FetchSwimlanesResponse,
      isInitialFetch = false,
    ) => {
      return response.reduce((acc, row) => {
        acc[row.rowId] = {
          extra: {
            id: row.rowId,
          },
          id: row.rowId,
          label: row.rowLabel,
          cols: Object.keys(row.columnData).reduce((rowAcc, colId) => {
            rowAcc[colId] = {
              id: colId,
              tasks:
                row.columnData[colId]?.tasks.map((task) => ({
                  colId,
                  extra: { ...task, id: task.taskId } as any,
                  id: task.taskId,
                  isDropped: true,
                  label: task.taskName,
                  rowId: row.rowId,
                  swimlaneId: row.rowId,
                })) ?? [],
              extra: {
                id: colId,
              },
              label: row.columnData[colId]?.colLabel ?? '',
              count: row.columnData[colId]?.innerTaskCount ?? 0,
            };

            return rowAcc;
          }, {} as Awaited<ReturnType<KanbanBoardProps['swimlaneFetch']>>['data']['0']['cols']),
          isInitialFetch,
        };

        return acc;
      }, {} as Awaited<ReturnType<KanbanBoardProps['swimlaneFetch']>>);
    };

    const swimlaneFetch: KanbanBoardProps['swimlaneFetch'] = useCallback(
      async ({ swimlaneIds }) => {
        const res = await KanbanApi.getTaskAcrossSwimlane({
          swimlaneIds: swimlaneIds as number[],
          columnGroup: columnField,
          rowGroup: rowField,
          fieldList: taskFieldsLookupById,
          filter,
          id: isSprint ? sprintId : projectDetail?.projectId,
          isSprint,
        });

        return getModifiedResponseForSwimlaneFetch(res);
      },
      [
        columnField,
        filter,
        rowField,
        taskFieldsLookupById,
        isSprint,
        sprintId,
        projectDetail?.projectId,
      ],
    );

    const initialswimlaneFetchForAddedTasks: () => Promise<
      UpdateSwimlaneParams<Task & { id: number }>
    > = useCallback(async () => {
      const res = await KanbanApi.getAddedTasksAcrossSwimlane({
        taskIds: selectedTasks,
        columnGroup: columnField,
        rowGroup: rowField,
        fieldList: taskFieldsLookupById,
        filter,
        isSprint,
      });

      return getModifiedResponseForSwimlaneFetch(res, true);
    }, [
      columnField,
      filter,
      rowField,
      taskFieldsLookupById,
      selectedTasks,
      isSprint,
    ]);

    const getValueForCustomField = useCallback(
      (fieldName, fieldValue) => {
        const customField = fields?.find(
          (field) => field.fieldName === fieldName,
        );

        if (customField?.customField) {
          return { fieldId: customField.fieldId, fieldValue };
        }

        return undefined;
      },
      [fields],
    );

    const getNewTaskParams: NewTasksDefaultParamsGetterCallbackType =
      useCallback(
        ({ colValue, swimlaneValue }) => {
          const result: ReturnType<NewTasksDefaultParamsGetterCallbackType> = {
            fields: [],
            project: projectOptions.find(
              (option) => option.value === projectId,
            ),
          };

          [
            { label: selectedColumnField, value: colValue },
            { label: selectedRowField, value: swimlaneValue },
          ].forEach(({ label, value }) => {
            switch (label?.toLowerCase()) {
              case 'project':
                result.project = projectOptions.find(
                  (option) => option.value === value,
                );
                break;
              case 'assignee':
                if (!result.assignee) {
                  result.assignee = { users: [] };
                }
                if (Number(value) > 0) {
                  result.assignee?.users.push({ userId: Number(value) });
                }
                break;
              case 'duedate':
                if (Number(value) > 0) {
                  result.startDateAndDueDate = [
                    null,
                    formatDate(Number(value)) as string,
                  ];
                }
                break;
              case 'priority':
                if (Number(value) > 0) {
                  result.fields?.push(getPriorityValue(Number(value)));
                }
                break;
              case 'status':
                if (Number(value) > 0) {
                  result.fields?.push(getStatusValue(Number(value)));
                }
                break;
              case 'project phase':
                result.projectPhase = value as string;
                break;
              case 'defaulttimeentrycategory':
                if (Number(value) > 0) {
                  result.defaultTimeEntryCategory = Number(value);
                }
                break;
              default: {
                const customField = getValueForCustomField(
                  label,
                  Number(value),
                );

                if (customField && Number(value) > 0) {
                  result.fields?.push(customField);
                }
              }
            }
          });

          return result;
        },
        [
          projectOptions,
          selectedColumnField,
          selectedRowField,
          projectId,
          getPriorityValue,
          getStatusValue,
          getValueForCustomField,
        ],
      );

    if (!columnField) {
      return null;
    }

    if (!mount) {
      return null;
    }

    if (statusesToShow?.length === 0 && isSprint) {
      return null;
    }

    return (
      <div
        ref={(r) => {
          ref.current = r;
          reCompute();
        }}
      >
        <AtomKanbanBoard<Task & { id: number }>
          onKanbanReady={onKanbanReady}
          handleDragAndDrop={handleDragAndDrop}
          layoutFetch={layoutFetch}
          isPaginated
          taskCardRenderer={TaskCardWrapper}
          swimlaneColumnFetch={swimlaneColumnFetch}
          swimlaneFetch={swimlaneFetch}
          height={height}
          taskCardLoadingRenderer={CardLoading}
          getNewTaskParams={getNewTaskParams}
          isColumnHidden={!!isColumnHidden}
          projectDetail={projectDetail}
          statusesToShow={statusesToShow}
          initialswimlaneFetchForAddedTasks={initialswimlaneFetchForAddedTasks}
          readOnly={readOnly}
        />
      </div>
    );
  },
);
